import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { UserCheck, RefreshCw, Send, ChevronRight, CheckCircle2 } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!t) return iso;
  const s = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); return `${d}d ago`;
}

export default function ReactivationNeedsHuman() {
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reactivation/needs-human");
      setLeads(data.leads || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openLead = async (lead) => {
    setSelected(lead);
    try {
      const { data } = await api.get(`/reactivation/leads/${lead.id}/messages`);
      setMessages(data.messages || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markHandled = async (leadId) => {
    setBusy(leadId);
    try {
      await api.post(`/reactivation/leads/${leadId}/mark-handled`);
      toast.success("Marked handled.");
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  return (
    <div className="p-10 max-w-7xl" data-testid="needs-human-page">
      <Toaster position="top-right" richColors />

      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><UserCheck className="w-3.5 h-3.5" /> CRM · Reactivation</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mb-2">Human follow-ups</h1>
          <p className="text-neutral-500 max-w-2xl text-sm">
            Leads where the AI hit a question it can't confidently answer. Jump in, mark handled when done.
          </p>
        </div>
        <button onClick={load} className="lf-btn-outline text-sm inline-flex items-center gap-2" data-testid="nh-refresh"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>

      {loading ? (
        <div className="text-neutral-500 text-sm">Loading…</div>
      ) : leads.length === 0 ? (
        <div className="border border-dashed border-neutral-300 p-16 text-center bg-white" data-testid="nh-empty">
          <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500 mb-3" />
          <div className="font-display text-2xl mb-2">Inbox zero</div>
          <p className="text-neutral-500">No leads currently need human follow-up. The AI's handling everything.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border border-neutral-200" data-testid="nh-list">
            {leads.map((l) => (
              <button key={l.id} onClick={() => openLead(l)}
                className={`w-full text-left px-4 py-3 border-b border-neutral-100 hover:bg-neutral-50 transition ${selected?.id === l.id ? "bg-neutral-50" : ""}`}
                data-testid={`nh-lead-${l.id.slice(0,8)}`}>
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{l.name || l.phone}</div>
                    <div className="text-xs text-neutral-500 truncate">{l.client_business_name} · {l.campaign_name}</div>
                    <div className="text-xs text-neutral-400 truncate mt-1">
                      <span className="font-mono uppercase tracking-widest text-[9px] mr-1">{l.last_message_role}:</span>
                      {l.last_message || "—"}
                    </div>
                  </div>
                  <div className="text-xs text-neutral-400 font-mono shrink-0">{timeAgo(l.last_touch_at)}</div>
                  <ChevronRight className="w-4 h-4 text-neutral-300" />
                </div>
              </button>
            ))}
          </div>

          <div className="bg-white border border-neutral-200 p-5" data-testid="nh-detail">
            {!selected ? (
              <div className="text-sm text-neutral-400 text-center py-16">Select a lead to see the transcript.</div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-3 mb-4 pb-3 border-b border-neutral-100">
                  <div>
                    <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">{selected.client_business_name} · {selected.campaign_name}</div>
                    <div className="text-lg font-bold">{selected.name || selected.phone}</div>
                    <div className="text-xs text-neutral-500 font-mono">{selected.phone} · {selected.inquired_about}</div>
                  </div>
                  <button onClick={() => markHandled(selected.id)} disabled={busy === selected.id}
                    className="lf-btn-primary text-xs px-3 py-2 inline-flex items-center gap-1" data-testid="nh-mark-handled">
                    <CheckCircle2 className="w-3.5 h-3.5" /> {busy === selected.id ? "…" : "Mark handled"}
                  </button>
                </div>
                <div className="space-y-2 max-h-[500px] overflow-y-auto" data-testid="nh-messages">
                  {messages.length === 0 && <div className="text-xs text-neutral-400 py-6 text-center">No messages yet.</div>}
                  {messages.map((m) => (
                    <div key={m.id} className={`flex ${m.role === "lead" ? "justify-end" : ""}`}>
                      <div className={`max-w-[85%] px-3 py-2 text-sm border ${m.role === "lead" ? "bg-neutral-100 border-neutral-200" : "bg-coral/5 border-coral/20 text-neutral-900"}`}>
                        <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-0.5">{m.role}</div>
                        {m.text}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
